import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from '@context/AuthContext';
import { PublicConfigProvider } from '@context/PublicConfigContext';
import { LocationProvider } from '@context/LocationContext';
import { ThemeProvider } from '@context/ThemeContext';
import { WishlistProvider } from '@context/WishlistContext';
import { NotificationProvider } from '@context/NotificationContext';
import AppLayout from '@components/layout/AppLayout';
import ProtectedRoute from '@components/layout/ProtectedRoute';
import AIChatAssistant from '@components/ai/AIChatAssistant';
import RebrandSessionToast from '@components/RebrandSessionToast';

import LandingPage from '@pages/LandingPage';
import LoginPage from '@pages/LoginPage';
import RegisterPage from '@pages/RegisterPage';
import WelcomePage from '@pages/WelcomePage';
import OnboardingPage from '@pages/OnboardingPage';
import FeedPage from '@pages/FeedPage';
import WishlistPage from '@pages/WishlistPage';
import MapPage from '@pages/MapPage';
import CreateActivityPage from '@pages/CreateActivityPage';
import ActivityDetailPage from '@pages/ActivityDetailPage';
import ProfilePage from '@pages/ProfilePage';
import ReviewsPage, { ReviewsIndexRedirect } from '@pages/ReviewsPage';
import MyActivitiesPage from '@pages/MyActivitiesPage';
import MessagesPage from '@pages/MessagesPage';
import PrivacyPolicyPage from '@pages/PrivacyPolicyPage';
import TermsOfServicePage from '@pages/TermsOfServicePage';
import DeleteAccountPage from '@pages/DeleteAccountPage';

const HomeRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) {
    return user.onboardingComplete ? <Navigate to="/feed" replace /> : <Navigate to="/onboarding" replace />;
  }
  return <LandingPage />;
};

/** Hide Sheri where it overlaps primary UI (onboarding, profile, map). */
const AI_CHAT_HIDDEN_PREFIXES = ['/profile'];
const AI_CHAT_HIDDEN_EXACT = new Set(['/onboarding', '/map']);

const AppChatAssistantGate = () => {
  const { pathname } = useLocation();
  if (AI_CHAT_HIDDEN_EXACT.has(pathname)) return null;
  if (AI_CHAT_HIDDEN_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`))) return null;
  return <AIChatAssistant />;
};

const CANONICAL_ORIGIN = 'https://yaarvi.com';

/** Single canonical URL for all routes: always yaarvi.com + current pathname. */
const RootCanonical = () => {
  const { pathname } = useLocation();
  const href = `${CANONICAL_ORIGIN}${pathname}`;
  return (
    <Helmet>
      <link rel="canonical" href={href} />
    </Helmet>
  );
};

const App = () => {
  return (
    <HelmetProvider>
    <ThemeProvider>
      <PublicConfigProvider>
        <AuthProvider>
          <LocationProvider>
            <Router>
              <RootCanonical />
              <NotificationProvider>
              <WishlistProvider>
                <Toaster
                  position="top-center"
                  toastOptions={{
                    duration: 3000,
                    style: {
                      borderRadius: '12px',
                      background: '#1e293b',
                      color: '#fff',
                      fontSize: '14px',
                    },
                  }}
                />

                <Routes>
                  <Route path="/" element={<HomeRedirect />} />
                  <Route path="/welcome" element={<WelcomePage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route
                    path="/onboarding"
                    element={
                      <ProtectedRoute>
                        <OnboardingPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route element={<AppLayout />}>
                    <Route path="/feed" element={<FeedPage />} />
                    <Route
                      path="/my-activities"
                      element={
                        <ProtectedRoute>
                          <MyActivitiesPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/wishlist" element={<WishlistPage />} />
                    <Route path="/map" element={<MapPage />} />
                    <Route
                      path="/create"
                      element={
                        <ProtectedRoute>
                          <CreateActivityPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/activity/:id/edit"
                      element={
                        <ProtectedRoute>
                          <CreateActivityPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/activity/:id" element={<ActivityDetailPage />} />
                    <Route
                      path="/profile"
                      element={
                        <ProtectedRoute>
                          <ProfilePage />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/profile/:id" element={<ProfilePage />} />
                    <Route path="/reviews" element={<ReviewsIndexRedirect />} />
                    <Route path="/reviews/:userId" element={<ReviewsPage />} />
                    <Route
                      path="/messages"
                      element={
                        <ProtectedRoute>
                          <MessagesPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/notifications" element={<Navigate to="/messages" replace />} />
                  </Route>

                  <Route path="/privacy" element={<PrivacyPolicyPage />} />
                  <Route path="/terms" element={<TermsOfServicePage />} />
                  <Route path="/delete-account" element={<DeleteAccountPage />} />

                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                <RebrandSessionToast />
                <AppChatAssistantGate />
              </WishlistProvider>
              </NotificationProvider>
            </Router>
          </LocationProvider>
        </AuthProvider>
      </PublicConfigProvider>
    </ThemeProvider>
    </HelmetProvider>
  );
};

export default App;
